/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { IComponentStyles } from '@fluentui/foundation-legacy';
import { CommandBarButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { ICommandBarItemProps, ICommandBarProps, ICommandBarStyles } from '@fluentui/react/lib/CommandBar';
import {
    ContextualMenuItemType,
    IContextualMenuItem,
    IContextualMenuItemStyleProps,
    IContextualMenuItemStyles,
    IContextualMenuStyleProps,
    IContextualMenuStyles
} from '@fluentui/react/lib/ContextualMenu';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logLandingPageProjectCreation } from 'src/core/services/telemetry/eventsNamesTemplate';
import { AppType } from 'src/core/stores/app/types';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { noop } from 'src/core/utils/base';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { routeTable } from 'src/core/utils/routeTable';

export type ProjectsCommandBarProps = TableCommandBarProps<object> & {
    isLoading?: boolean;
    showDelete?: boolean;
    showFarItems?: boolean;
    isDeleteEnabled?: boolean;
    displayedItemsCount: number;
    onDeleteProject?: () => void;
    filterProjectTypes?: AppType[];
    onChangeFilterProjectTypes?: (newFilters: AppType[]) => void;
};

const messages = defineMessages({
    filter: { id: 'languageStudio.projectsCommandBar.filter', defaultMessage: 'Filter' },
    project: { id: 'languageStudio.projectsCommandBar.project', defaultMessage: 'project(s)' },
    createNew: { id: 'languageStudio.projectsCommandBar.createNew', defaultMessage: 'Create new' },
    delete: { id: 'languageStudio.projectsCommandBar.delete', defaultMessage: 'Delete' },
    qna: { id: 'languageStudio.projectsCommandBar.qna', defaultMessage: 'Custom question answering' },
    projectType: { id: 'languageStudio.projectTypesCommandBar.project', defaultMessage: 'Project type' },
    searchPlaceholder: { id: 'languageStudio.projectsCommandBar.searchPlaceholder', defaultMessage: 'Search' },
    orchestration: { id: 'languageStudio.projectsCommandBar.orchestration', defaultMessage: 'Orchestration workflow' },
    extraction: { id: 'languageStudio.projectsCommandBar.extraction', defaultMessage: 'Custom named entity recognition' },
    classification: { id: 'languageStudio.projectsCommandBar.classification', defaultMessage: 'Custom text classification' },
    abstractiveSummarization: {
        id: 'languageStudio.projectsCommandBar.abstractiveSummarization',
        defaultMessage: 'Custom abstractive summarization'
    },
    conversationSummarization: {
        id: 'languageStudio.projectsCommandBar.conversationSummarization',
        defaultMessage: 'Custom conversation summarization'
    },
    conversation: { id: 'languageStudio.projectsCommandBar.conversation', defaultMessage: 'Conversational language understanding' },
    deleteTooltip: {
        id: 'languageStudio.projectsCommandBar.deleteTooltip',
        defaultMessage: 'All selected projects have to be of the same type'
    },
    conversationDescription: {
        id: 'languageStudio.projectsCommandBar.conversationDescription',
        defaultMessage: 'Build natural language into apps, bots, and IoT devices.'
    },
    orchestrationDescription: {
        id: 'languageStudio.projectsCommandBar.orchestrationDescription',
        defaultMessage: 'Connect and orchestrate CLU, Custom question answering & LUIS projects together in one single project'
    },
    qnaDescription: {
        id: 'languageStudio.projectsCommandBar.qnaDescription',
        defaultMessage:
            'Customize the list of questions and answers extracted from your content\ncorpus to provide a conversational experience that suits your needs.'
    },
    extractionDescription: {
        id: 'languageStudio.projectsCommandBar.extractionDescription',
        defaultMessage: 'Train an extraction model to identify your domain categories using your own data.'
    },
    classificationDescription: {
        id: 'languageStudio.projectsCommandBar.classificationDescription',
        defaultMessage: 'Train a classification model to classify text using your own data.'
    },
    abstractiveSummarizationDescription: {
        id: 'languageStudio.projectsCommandBar.abstractiveSummarizationDescription',
        defaultMessage: 'Train a summarization model to create or generate summaries of documents.'
    },
    conversationSummarizationDescription: {
        id: 'languageStudio.projectsCommandBar.conversationSummarizationDescription',
        defaultMessage: 'Train a summarization model to create or generate summaries of conversations.'
    }
});

const commandBarTheme = getScopedTheme('ProjectsCommandBar');
const menuButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ menuIcon: { display: 'none' } });
const headerStyles = classNamesFunction<IContextualMenuItemStyleProps, IContextualMenuItemStyles>()({ checkmarkIcon: { display: 'none' } });
const menuStyles = classNamesFunction<IContextualMenuStyleProps, IContextualMenuStyles>()({
    header: { color: commandBarTheme.defaultTextColor }
});
const commandBarStyles = classNamesFunction<ICommandBarProps, ICommandBarStyles>()({
    root: { borderBottom: `1px solid ${commandBarTheme.topBorderColor}` }
});
const filterButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    flexContainer: { justifyContent: 'flex-start' },
    root: { minHeight: '36px', width: '100%', paddingLeft: '8px' }
});

const projectItemStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { borderBottom: `${commandBarTheme.bottomBorderColor} solid 1px`, width: '100%', padding: '10px', cursor: 'pointer' }
});

export const getProjectCreationItems = (): IContextualMenuItem[] => {
    const { localize } = useLocalization();
    const history = useHistory();

    const allowedKeys = FeatureToggles.EnableCustomSkills
        ? ['createConversation', 'createClassification', 'createExtraction', 'createQnA', 'createOrchestration']
        : FeatureToggles.EnableCLUCustomSkills
        ? ['createConversation', 'createQnA', 'createOrchestration']
        : ['createQnA'];
    const visibleKeys = [
        ...allowedKeys,
        ...(FeatureToggles.CustomSummarization ? ['createAbstractiveSummarization', 'createConversationSummarization'] : [])
    ];

    return [
        {
            key: 'createConversation',
            onRender: () => (
                <Stack
                    styles={projectItemStyles}
                    data-is-focusable
                    onClick={() => {
                        logLandingPageProjectCreation('clu');
                        history.push(routeTable.clu.projectsPage(), true);
                    }}>
                    <LuisText>{localize(messages.conversation)}</LuisText>
                    <TruncatedText
                        size="12px"
                        minWidth={400}
                        color={commandBarTheme.secondaryColor}
                        title={localize(messages.conversationDescription)}>
                        {localize(messages.conversationDescription)}
                    </TruncatedText>
                </Stack>
            )
        },
        {
            key: 'createOrchestration',
            onRender: () => (
                <Stack
                    styles={projectItemStyles}
                    data-is-focusable
                    onClick={() => {
                        logLandingPageProjectCreation('orchestration');
                        history.push(routeTable.orchestration.projectsPage(), true);
                    }}>
                    <LuisText>{localize(messages.orchestration)}</LuisText>
                    <TruncatedText
                        size="12px"
                        minWidth={400}
                        color={commandBarTheme.secondaryColor}
                        title={localize(messages.orchestrationDescription)}>
                        {localize(messages.orchestrationDescription)}
                    </TruncatedText>
                </Stack>
            )
        },
        {
            key: 'createQnA',
            onRender: () => (
                <Stack
                    styles={projectItemStyles}
                    data-is-focusable
                    onClick={() => {
                        logLandingPageProjectCreation('qnaMaker');
                        history.push(routeTable.qnaMaker.projectsPage(), true);
                    }}>
                    <LuisText>{localize(messages.qna)}</LuisText>
                    <TruncatedText
                        size="12px"
                        minWidth={400}
                        color={commandBarTheme.secondaryColor}
                        title={localize(messages.qnaDescription)}>
                        {localize(messages.qnaDescription)}
                    </TruncatedText>
                </Stack>
            )
        },
        {
            key: 'createClassification',
            onRender: () => (
                <Stack
                    styles={projectItemStyles}
                    data-is-focusable
                    onClick={() => {
                        logLandingPageProjectCreation('classification');
                        history.push(routeTable.customText.classificationProjectsPage(), true);
                    }}>
                    <LuisText>{localize(messages.classification)}</LuisText>
                    <TruncatedText
                        size="12px"
                        minWidth={400}
                        color={commandBarTheme.secondaryColor}
                        title={localize(messages.classificationDescription)}>
                        {localize(messages.classificationDescription)}
                    </TruncatedText>
                </Stack>
            )
        },
        {
            key: 'createExtraction',
            onRender: () => (
                <Stack
                    styles={projectItemStyles}
                    data-is-focusable
                    onClick={() => {
                        logLandingPageProjectCreation('ner');
                        history.push(routeTable.customText.extractionProjectsPage(), true);
                    }}>
                    <LuisText>{localize(messages.extraction)}</LuisText>
                    <TruncatedText
                        size="12px"
                        minWidth={400}
                        color={commandBarTheme.secondaryColor}
                        title={localize(messages.extractionDescription)}>
                        {localize(messages.extractionDescription)}
                    </TruncatedText>
                </Stack>
            )
        }
    ].filter(i => visibleKeys.includes(i.key));
};

/**
 * Displays the available commands to the language studio projects.
 */
export const ProjectsCommandBar = observer((props: ProjectsCommandBarProps) => {
    const {
        query,
        onQueryChange,
        showDelete = true,
        isLoading = false,
        displayedItemsCount,
        showFarItems = true,
        onDeleteProject = noop,
        isDeleteEnabled = false,
        filterProjectTypes = [],
        onChangeFilterProjectTypes
    } = props;
    const { localize } = useLocalization();

    const { rootStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const itemsCountText = `${displayedItemsCount} ${localize(messages.project)}`;

    const renderFilterItem = (text: string, appTypes: AppType[], disabled?: boolean) => (
        <CommandBarButton
            role="menuitem"
            disabled={disabled}
            styles={filterButtonStyles}
            aria-label={text}
            onClick={() =>
                onChangeFilterProjectTypes(
                    appTypes.every(appType => filterProjectTypes.every(f => f !== appType))
                        ? [...filterProjectTypes, ...appTypes]
                        : filterProjectTypes.filter(f => appTypes.every(a => a !== f))
                )
            }>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                <Checkbox disabled={disabled} checked={!disabled && filterProjectTypes.some(f => appTypes.some(a => a === f))} />
                <LuisText>{text}</LuisText>
            </Stack>
        </CommandBarButton>
    );

    const items: ICommandBarItemProps[] = [
        {
            key: 'create',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="createNewProjectButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            aria-haspopup="menu"
                            iconProps={{ iconName: 'Add' }}
                            text={localize(messages.createNew)}
                            name={localize(messages.createNew)}
                            disabled={!canWrite || item.disabled}
                            ariaLabel={localize(messages.createNew)}
                            data-automation-id="createNewProjectButton"
                            menuProps={{ items: getProjectCreationItems() }}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        ...(showDelete
            ? [
                  {
                      key: 'delete',
                      onRender: (item: ICommandBarItemProps) => (
                          <RBACTooltip
                              displayedUserRole={displayedUserRole}
                              hidden={canWrite || item.disabled}
                              data-automation-id="deleteProjectButtonLanguageToolTip"
                              componentSurroundedByToolTip={
                                  <LuisTooltip hidden={isDeleteEnabled} content={localize(messages.deleteTooltip)}>
                                      <CommandBarButton
                                          onClick={onDeleteProject}
                                          text={localize(messages.delete)}
                                          name={localize(messages.delete)}
                                          iconProps={{ iconName: 'Delete' }}
                                          data-automation-id="deleteProjectButton"
                                          disabled={!canWrite || !isDeleteEnabled || item.disabled}
                                          styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                                      />
                                  </LuisTooltip>
                              }
                          />
                      )
                  }
              ]
            : [])
    ];

    const allowedKeys = FeatureToggles.EnableCustomSkills
        ? ['conversation', 'orchestration', 'extraction', 'classification', 'qna']
        : FeatureToggles.EnableCLUCustomSkills
        ? ['conversation', 'orchestration', 'qna']
        : ['qna'];
    const visibleKeys = [
        ...allowedKeys,
        ...(FeatureToggles.CustomSummarization ? ['abstractiveSummarization', 'conversationSummarization'] : [])
    ];

    const projectItems = [
        {
            key: 'conversation',
            onRender: () => renderFilterItem(localize(messages.conversation), ['clu'])
        },
        {
            key: 'orchestration',
            onRender: () => renderFilterItem(localize(messages.orchestration), ['Orchestration'])
        },
        { key: 'qna', onRender: () => renderFilterItem(localize(messages.qna), ['qnaMaker']) },
        { key: 'extraction', onRender: () => renderFilterItem(localize(messages.extraction), ['CustomEntityRecognition']) },
        {
            key: 'classification',
            onRender: () =>
                renderFilterItem(localize(messages.classification), ['CustomSingleLabelClassification', 'CustomMultiLabelClassification'])
        }
    ].filter(i => visibleKeys.includes(i.key));

    const filtersItems: IContextualMenuItem[] = [
        {
            key: 'projectTypeHeader',
            itemProps: { styles: headerStyles },
            text: localize(messages.projectType),
            itemType: ContextualMenuItemType.Header
        },
        { key: 'projectTypeHeaderDivider', itemType: ContextualMenuItemType.Divider },
        ...projectItems
    ];

    const farItems: ICommandBarItemProps[] = [
        {
            key: 'itemsCount',
            onRender: () =>
                isLoading ? (
                    <Shimmer
                        shimmerElements={[{ type: ShimmerElementType.line, width: '70px', height: 15 }]}
                        styles={{ root: { marginTop: '15px', marginRight: '10px' } }}
                    />
                ) : (
                    <LuisText
                        styles={{
                            root: { display: 'flex', alignItems: 'center', marginRight: '5px' }
                        }}>
                        {itemsCountText}
                    </LuisText>
                )
        },
        {
            key: 'searchBox',
            onRender: () => (
                <LuisDebouncedCollapsableSearchBox
                    size={36}
                    value={query}
                    debounceTime={200}
                    ariaLabel={localize(messages.searchPlaceholder)}
                    placeholder={localize(messages.searchPlaceholder)}
                    onChange={(_, newValue?: string) => onQueryChange(newValue)}
                />
            )
        },
        {
            key: 'filter',
            buttonStyles: menuButtonStyles,
            text: localize(messages.filter),
            iconProps: { iconName: 'Filter' },
            subMenuProps: { items: filtersItems, styles: menuStyles }
        }
    ];

    return <LuisCommandBar items={items} farItems={showFarItems ? farItems : []} styles={showFarItems && commandBarStyles} />;
});
